/**
 * HexQuickLinks - Shared sidebar navigation component for hex-related pages.
 * Styled as elevator buttons with yellow indicator lights.
 */

import { useState } from 'react';
import { Link } from '@inertiajs/react';

/**
 * Navigation item definition.
 */
interface QuickLinkItem {
    id: string;
    label: string;
    href: string;
    tab?: string;
    ownerOnly?: boolean;
    hideOnOpenHex?: boolean;
    enclaveOnly?: boolean;
}

/**
 * Props for the HexQuickLinks component.
 */
interface HexQuickLinksProps {
    /** The hex ID for building URLs */
    hexId: number;
    /** Whether the current user is the hex owner */
    isOwner: boolean;
    /** Whether this is an open hex (some links hidden) */
    isOpenHex?: boolean;
    /** Whether this is an enclave hex (has reputation) */
    isEnclave?: boolean;
    /** Currently active item ID (for highlighting) */
    activeItem?: string;
    /** Enable collapsible behavior */
    collapsible?: boolean;
    /** Default expanded state when collapsible */
    defaultExpanded?: boolean;
    /** Callback for tab-based navigation (View.tsx) */
    onTabChange?: (tab: string) => void;
    /** Callback to close sidebar on mobile */
    closeSidebar?: () => void;
    /** Whether viewport is mobile */
    isMobile?: boolean;
    /** Badge count for vault items */
    vaultCount?: number;
    /** Badge count for in-progress attempts */
    inProgressCount?: number;
}

/**
 * Get the list of quick link items for hex navigation.
 */
function getQuickLinkItems(hexId: number): QuickLinkItem[] {
    return [
        {
            id: 'submap',
            label: 'The Map',
            href: `/hex/${hexId}?tab=submap`,
            tab: 'submap',
        },
        {
            id: 'library',
            label: 'Library',
            href: `/hex/${hexId}`,
            tab: 'library',
        },
        {
            id: 'reputation',
            label: 'Reputation',
            href: `/hex/${hexId}?tab=reputation`,
            tab: 'reputation',
            enclaveOnly: true,
        },
        {
            id: 'buildings',
            label: 'Buildings',
            href: `/hex/${hexId}?tab=buildings`,
            tab: 'buildings',
            hideOnOpenHex: true,
        },
        {
            id: 'characters',
            label: 'Characters',
            href: `/hex/${hexId}?tab=characters`,
            tab: 'characters',
            hideOnOpenHex: true,
        },
        {
            id: 'vault',
            label: 'Vault',
            href: `/hex/${hexId}?tab=vault`,
            tab: 'vault',
            ownerOnly: true,
            hideOnOpenHex: true,
        },
        {
            id: 'crafting',
            label: 'Crafting',
            href: `/hex/${hexId}/crafting`,
            ownerOnly: true,
            hideOnOpenHex: true,
        },
        {
            id: 'manage',
            label: 'Manage',
            href: `/hex/${hexId}?tab=manage`,
            tab: 'manage',
            ownerOnly: true,
            hideOnOpenHex: true,
        },
        {
            id: 'adventures',
            label: 'Adventures',
            href: `/hex/${hexId}?tab=adventures`,
            tab: 'adventures',
            ownerOnly: true,
            hideOnOpenHex: true,
        },
        {
            id: 'attempts',
            label: 'Results',
            href: `/hex/${hexId}?tab=attempts`,
            tab: 'attempts',
            ownerOnly: true,
            hideOnOpenHex: true,
        },
    ];
}

/**
 * Chevron icon for collapsible header.
 */
function ChevronIcon({ expanded }: { expanded: boolean }) {
    return (
        <svg
            className={`w-4 h-4 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
        >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
    );
}

/**
 * Yellow indicator light component for elevator buttons.
 */
function IndicatorLight({ active, hover }: { active: boolean; hover: boolean }) {
    return (
        <div
            className="relative w-2 h-4 rounded-sm flex-shrink-0"
            style={{
                background: active || hover
                    ? 'linear-gradient(180deg, #FCD34D 0%, #F59E0B 50%, #D97706 100%)'
                    : 'linear-gradient(180deg, #4A4A3A 0%, #3A3A2A 50%, #2A2A1A 100%)',
                boxShadow: active || hover
                    ? '0 0 8px rgba(252, 211, 77, 0.8), 0 0 16px rgba(252, 211, 77, 0.4), inset 0 1px 0 rgba(255,255,255,0.4)'
                    : 'inset 0 1px 2px rgba(0,0,0,0.4)',
                border: '1px solid #2A2A1A',
                transition: 'all 0.2s ease',
            }}
        >
            {/* Bulb reflection */}
            {(active || hover) && (
                <div
                    className="absolute top-0.5 left-0.5 w-1 h-1 rounded-full"
                    style={{
                        background: 'rgba(255, 255, 255, 0.6)',
                    }}
                />
            )}
        </div>
    );
}

/**
 * Green status light component - indicates items are present.
 */
function GreenStatusLight({ active }: { active: boolean }) {
    return (
        <div
            className="relative w-2 h-2 rounded-full flex-shrink-0"
            style={{
                background: active
                    ? 'radial-gradient(circle at 30% 30%, #4ADE80 0%, #22C55E 50%, #16A34A 100%)'
                    : 'radial-gradient(circle at 30% 30%, #3D4A3A 0%, #2D3A2A 50%, #1D2A1A 100%)',
                boxShadow: active
                    ? '0 0 6px rgba(74, 222, 128, 0.8), 0 0 12px rgba(74, 222, 128, 0.4), inset 0 1px 0 rgba(255,255,255,0.3)'
                    : 'inset 0 1px 2px rgba(0,0,0,0.4)',
                border: active ? '1px solid #22C55E' : '1px solid #2A2A1A',
                transition: 'all 0.2s ease',
            }}
        >
            {/* Bulb reflection when active */}
            {active && (
                <div
                    className="absolute top-0 left-0.5 w-1 h-0.5 rounded-full"
                    style={{
                        background: 'rgba(255, 255, 255, 0.5)',
                    }}
                />
            )}
        </div>
    );
}

/**
 * Elevator button component.
 */
function ElevatorButton({
    item,
    isActive,
    hasNotification,
    onClick,
}: {
    item: QuickLinkItem;
    isActive: boolean;
    hasNotification?: boolean;
    onClick: (e: React.MouseEvent) => void;
}) {
    const [isHovered, setIsHovered] = useState(false);

    const buttonContent = (
        <div
            className="relative flex items-center gap-2 px-3 py-2 rounded cursor-pointer transition-all"
            style={{
                background: isActive
                    ? 'linear-gradient(180deg, #4A4540 0%, #3A3530 50%, #2A2520 100%)'
                    : 'linear-gradient(180deg, #3A3530 0%, #2A2520 50%, #1A1510 100%)',
                border: '2px solid #5A5550',
                boxShadow: isActive
                    ? 'inset 0 2px 4px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.1)'
                    : isHovered
                        ? '0 2px 4px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)'
                        : 'inset 0 1px 2px rgba(0,0,0,0.2), 0 1px 0 rgba(255,255,255,0.05)',
                transform: isActive ? 'translateY(1px)' : 'translateY(0)',
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Yellow indicator light */}
            <IndicatorLight active={isActive} hover={isHovered} />

            {/* Button label */}
            <span
                className="text-sm font-medium flex-1"
                style={{
                    color: isActive ? '#F5DEB3' : isHovered ? '#E8D8C8' : '#B8A898',
                }}
            >
                {item.label}
            </span>

            {/* Green status light - shows when items are present */}
            {hasNotification !== undefined && (
                <GreenStatusLight active={hasNotification} />
            )}

            {/* Metal texture overlay */}
            <div
                className="absolute inset-0 rounded opacity-10 pointer-events-none"
                style={{
                    backgroundImage: 'repeating-linear-gradient(90deg, transparent 0px, transparent 1px, rgba(255,255,255,0.02) 1px, rgba(255,255,255,0.02) 2px)',
                }}
            />
        </div>
    );

    return (
        <Link
            href={item.href}
            onClick={onClick}
            className="block"
        >
            {buttonContent}
        </Link>
    );
}

/**
 * HexQuickLinks - Consistent sidebar navigation for hex pages.
 * Styled as elevator buttons with yellow indicator lights.
 */
export function HexQuickLinks({
    hexId,
    isOwner,
    isOpenHex = false,
    isEnclave = false,
    activeItem,
    collapsible = false,
    defaultExpanded = true,
    onTabChange,
    closeSidebar,
    isMobile = false,
    vaultCount,
    inProgressCount,
}: HexQuickLinksProps) {
    const [expanded, setExpanded] = useState(defaultExpanded);

    const items = getQuickLinkItems(hexId);

    // Filter items based on owner status, open hex, and enclave status
    const visibleItems = items.filter((item) => {
        if (item.ownerOnly && !isOwner) return false;
        if (item.hideOnOpenHex && isOpenHex) return false;
        if (item.enclaveOnly && !isEnclave) return false;
        return true;
    });

    // Handle click on a navigation item
    const handleItemClick = (item: QuickLinkItem, e: React.MouseEvent) => {
        // If we have a tab change handler and the item has a tab, use tab navigation
        if (onTabChange && item.tab) {
            e.preventDefault();
            onTabChange(item.tab);
            if (isMobile && closeSidebar) {
                closeSidebar();
            }
        } else {
            // For Link-based navigation, just close sidebar on mobile
            if (isMobile && closeSidebar) {
                closeSidebar();
            }
        }
    };

    // Check if an item should show a notification light
    const hasNotification = (itemId: string): boolean | undefined => {
        if (itemId === 'vault') return vaultCount !== undefined ? vaultCount > 0 : undefined;
        if (itemId === 'attempts') return inProgressCount !== undefined ? inProgressCount > 0 : undefined;
        return undefined;
    };

    return (
        <div className="p-3">
            {/* Elevator panel frame */}
            <div
                className="relative rounded-lg overflow-hidden"
                style={{
                    background: 'linear-gradient(145deg, #2A2520 0%, #1A1510 100%)',
                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)',
                    border: '3px solid #4A4540',
                }}
            >
                {/* Panel header */}
                {collapsible ? (
                    <button
                        onClick={() => setExpanded(!expanded)}
                        className="w-full flex items-center justify-between px-3 py-2 cursor-pointer transition-colors"
                        style={{
                            background: 'linear-gradient(180deg, rgba(90,85,80,0.3) 0%, rgba(74,69,64,0.2) 100%)',
                            borderBottom: expanded ? '1px solid rgba(90,85,80,0.3)' : 'none',
                        }}
                    >
                        <span
                            className="text-xs font-bold uppercase tracking-wider"
                            style={{ color: '#B8A898' }}
                        >
                            Quick Links
                        </span>
                        <ChevronIcon expanded={expanded} />
                    </button>
                ) : (
                    <div
                        className="px-3 py-2"
                        style={{
                            background: 'linear-gradient(180deg, rgba(90,85,80,0.3) 0%, rgba(74,69,64,0.2) 100%)',
                            borderBottom: '1px solid rgba(90,85,80,0.3)',
                        }}
                    >
                        <h2
                            className="text-xs font-bold uppercase tracking-wider"
                            style={{ color: '#B8A898' }}
                        >
                            Quick Links
                        </h2>
                    </div>
                )}

                {/* Navigation items */}
                {collapsible ? (
                    <div
                        className={`overflow-hidden transition-all duration-200 ease-in-out ${
                            expanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                        }`}
                    >
                        <nav className="p-2 space-y-1.5">
                            {visibleItems.map((item) => (
                                <ElevatorButton
                                    key={item.id}
                                    item={item}
                                    isActive={activeItem === item.id}
                                    hasNotification={hasNotification(item.id)}
                                    onClick={(e) => handleItemClick(item, e)}
                                />
                            ))}
                        </nav>
                    </div>
                ) : (
                    <nav className="p-2 space-y-1.5">
                        {visibleItems.map((item) => (
                            <ElevatorButton
                                key={item.id}
                                item={item}
                                isActive={activeItem === item.id}
                                hasNotification={hasNotification(item.id)}
                                onClick={(e) => handleItemClick(item, e)}
                            />
                        ))}
                    </nav>
                )}

                {/* Corner screws/rivets for industrial look */}
                <div
                    className="absolute top-1.5 left-1.5 w-1.5 h-1.5 rounded-full"
                    style={{ background: 'radial-gradient(circle at 30% 30%, #6A6560, #3A3530)' }}
                />
                <div
                    className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full"
                    style={{ background: 'radial-gradient(circle at 30% 30%, #6A6560, #3A3530)' }}
                />
                <div
                    className="absolute bottom-1.5 left-1.5 w-1.5 h-1.5 rounded-full"
                    style={{ background: 'radial-gradient(circle at 30% 30%, #6A6560, #3A3530)' }}
                />
                <div
                    className="absolute bottom-1.5 right-1.5 w-1.5 h-1.5 rounded-full"
                    style={{ background: 'radial-gradient(circle at 30% 30%, #6A6560, #3A3530)' }}
                />
            </div>
        </div>
    );
}
